import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift, Copy, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

export default function VoucherDialog({
  trigger,
  testIdPrefix = "voucher",
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showSkipToSchedule = false,
}) {
  const { t } = useT();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = isControlled ? (controlledOnOpenChange || (() => {})) : setUncontrolledOpen;
  const [loading, setLoading] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const reset = () => {
    setVoucher(null);
    setCopied(false);
    setForm({ name: "", email: "", phone: "" });
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(reset, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/vouchers", form);
      setVoucher(data);
      toast.success("Voucher unlocked! Show this code at pickup.");
    } catch {
      toast.error("Could not generate voucher. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!voucher?.code) return;
    try {
      await navigator.clipboard.writeText(voucher.code);
      setCopied(true);
      toast.success("Code copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!isControlled && (
        <DialogTrigger asChild data-testid={`${testIdPrefix}-trigger`}>
          {trigger || (
            <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white h-11 px-6 font-semibold gap-2 shadow-[0_12px_24px_-12px_rgba(245,158,11,0.7)]">
              <Gift className="h-4 w-4" /> {t("cta.claimTenVoucher")}
            </Button>
          )}
        </DialogTrigger>
      )}

      <DialogContent
        data-testid={`${testIdPrefix}-dialog`}
        className="sm:max-w-md rounded-3xl border-slate-200 p-0 overflow-hidden"
      >
        {/* Warm header */}
        <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 px-7 pt-8 pb-7 border-b border-amber-100">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em]">
            <Sparkles className="h-3 w-3" /> {t("voucher.tag")}
          </span>
          <DialogHeader className="mt-3 text-left space-y-2">
            <DialogTitle className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-brand-navy">
              {t("voucher.dialog.title")}
            </DialogTitle>
            <DialogDescription className="text-base text-slate-600 leading-relaxed">
              {t("voucher.dialog.desc")}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="px-7 py-6">
          {voucher ? (
            <div className="text-center">
              <div className="inline-flex h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 items-center justify-center mb-4">
                <Check className="h-7 w-7" />
              </div>
              <p className="text-sm text-slate-500">{t("voucher.dialog.code")}</p>
              <div
                data-testid={`${testIdPrefix}-code`}
                className="mt-2 inline-flex items-center gap-3 rounded-2xl bg-brand-navy text-white px-5 py-3 font-display text-xl tracking-[0.2em] font-bold"
              >
                {voucher.code}
                <button
                  onClick={copy}
                  data-testid={`${testIdPrefix}-copy-btn`}
                  className="rounded-full bg-white/10 hover:bg-white/20 p-1.5 transition-colors"
                  aria-label="Copy code"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-300" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
              <p className="mt-5 text-sm text-slate-600 leading-relaxed">
                {t("voucher.dialog.mention")}
              </p>
              <Button
                data-testid={`${testIdPrefix}-schedule-btn`}
                asChild
                className="mt-6 w-full rounded-full bg-brand-blue hover:bg-brand-navy text-white h-11"
                onClick={() => setOpen(false)}
              >
                <a href="#schedule">{t("voucher.dialog.scheduleFirst")}</a>
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid={`${testIdPrefix}-form`}>
              <div>
                <Label htmlFor="v-name" className="text-brand-navy font-semibold">
                  {t("voucher.dialog.fullName")}
                </Label>
                <Input
                  id="v-name"
                  data-testid={`${testIdPrefix}-input-name`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="mt-2 h-11"
                  required
                />
              </div>
              <div>
                <Label htmlFor="v-email" className="text-brand-navy font-semibold">
                  {t("voucher.dialog.email")}
                </Label>
                <Input
                  id="v-email"
                  data-testid={`${testIdPrefix}-input-email`}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  className="mt-2 h-11"
                  required
                />
              </div>
              <div>
                <Label htmlFor="v-phone" className="text-brand-navy font-semibold">
                  {t("voucher.dialog.phone")}{" "}
                  <span className="text-slate-400 font-normal">
                    {t("voucher.dialog.optional")}
                  </span>
                </Label>
                <Input
                  id="v-phone"
                  data-testid={`${testIdPrefix}-input-phone`}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(480) 555-1234"
                  className="mt-2 h-11"
                />
              </div>

              <Button
                type="submit"
                data-testid={`${testIdPrefix}-submit-btn`}
                disabled={loading}
                className="w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white h-11 font-semibold gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> …
                  </>
                ) : (
                  <>
                    <Gift className="h-4 w-4" /> {t("cta.claimTenVoucher")}
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                {t("voucher.dialog.disclaimer")}
              </p>
              {showSkipToSchedule && (
                <div className="pt-1 text-center">
                  <a
                    href="#schedule"
                    data-testid={`${testIdPrefix}-skip-btn`}
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center text-sm font-semibold text-slate-600 hover:text-brand-navy underline underline-offset-4 decoration-2 decoration-slate-300 hover:decoration-brand-navy transition-colors"
                  >
                    No thanks — schedule a pickup
                  </a>
                </div>
              )}
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
